import React, { Component } from 'react'
import compose from 'lodash/flowRight'
import { withTranslation, WithTranslation } from 'react-i18next'

import { ChildProps } from 'react-apollo'
import withPositionAlternativesDataQuery, {
  InputProps,
  Response,
} from './positionAlternativesQuery'

import get from 'lodash/get'
import isNil from 'lodash/isNil'
import set from 'lodash/set'
import defaultTo from 'lodash/defaultTo'

import { IEditableTableHead } from './head'
import EditableTableData, { IAlternative } from './editableTableData'
import { IItem } from './editableTableData'

import { IEditableItem, IEditableData } from 'components/layout/editableTable'
import TableData from 'components/layout/table/tableData'
import Button from 'components/ui/button'
import Radio from 'components/ui/radio'

import { saveModeOptions } from 'data'

export enum SaveMode {
  Calculation = '',
  Project = 'p',
  Permanent = 'a',
}

interface IProps extends ChildProps<InputProps, Response>, WithTranslation {
  positionAlternativesData: any
  item: IEditableItem
  head: IEditableTableHead[]
  onSave?: (saveMode: SaveMode, item: IEditableItem) => void
}

interface IState {
  isActive: boolean
  isEditable: boolean
  item: IEditableItem
  alternatives?: IAlternative[]
  saveMode: SaveMode
}

class EditableTableRow extends Component<IProps, IState> {
  constructor(props: IProps) {
    super(props)
    this.state = {
      isActive: false,
      isEditable: this.isEditable(props.head, props.item),
      item: props.item,
      saveMode: SaveMode.Calculation,
    }
  }

  componentWillReceiveProps = (nextProps: IProps) => {
    if (nextProps.item !== this.state.item) {
      this.setState({ item: nextProps.item })
    }
  }

  componentDidMount = () => {
    const { head } = this.props
    const { item } = this.props

    const articleNumber = get(item, 'data.articleNumber', undefined)
    const hasAlternatives = head.find((headItem: IEditableTableHead) => headItem.hasOptions)

    if (hasAlternatives && !isNil(articleNumber)) {
      this.props.positionAlternativesData
        .refetch({
          amount: get(item, 'data.amount.value', 0),
          artnr: articleNumber,
          clippingAmount: get(item, 'data.amountWithClipping.value', undefined),
        })
        .then((res: any) => {
          const currentProduct = {
            articleNumber,
            product: item.productName,
            totalPrice: get(item, 'data.totalPrice', 0),
            unitPrice: get(item, 'data.unitPrice', 0),
          }

          this.setState({
            alternatives: [currentProduct, ...get(res, 'data.getPositionAlternatives', [])],
          })
        })
        .catch(() => {
          this.setState({
            alternatives: [],
          })
        })
    }
  }

  // Returns true if at least one data field is editable or one column is set as editable
  isEditable = (head: IEditableTableHead[], item: IEditableItem) => {
    const editable = head.find((headItem) => {
      return headItem.editable === true
    })
      ? true
      : false

    if (!editable && item.data) {
      return Object.values(item.data).find((i) => {
        return i.editable === true
      })
        ? true
        : false
    }
    return editable
  }

  startEdit = () => {
    this.setState({
      ...this.state,
      isActive: true,
    })
  }

  cancelEdit = () => {
    this.setState({
      ...this.state,
      item: {
        ...this.props.item,
      },
      isActive: false,
    })
  }

  handleInputChange = (key: string, data: IItem) => {
    this.setState({
      ...this.state,
      item: {
        ...this.state.item,
        data: {
          ...this.state.item.data,
          [key]: data,
        },
      },
    })
  }

  handleSaveModeChange = (mode: string) => {
    const saveMode: SaveMode = mode as SaveMode
    this.setState({ saveMode })
  }

  handleSave = () => {
    const { onSave } = this.props
    const { saveMode, item } = this.state

    if (onSave) onSave(saveMode, item)

    this.cancelEdit()
  }

  // Updates the current item according to the selected alternative
  handleAlternativeChange = (alternative: IAlternative) => {
    const { articleNumber, product, unitPrice } = alternative
    const newItem = this.state.item
    const amount = defaultTo(get(this.state, 'item.data.amount.value'), 0)
    const newTotalPrice = amount * unitPrice

    set(newItem, 'productName', product)
    set(newItem, 'articleNumber', articleNumber)
    set(newItem, 'data.articleNumber', articleNumber)
    set(newItem, 'data.product', product)
    set(newItem, 'data.unitPrice', unitPrice)
    set(newItem, 'data.totalPrice', newTotalPrice)

    this.setState({
      ...this.state,
      item: {
        ...newItem,
      },
    })
  }

  getItem = (key: string) => {
    if (this.props.item.data && this.props.item.data[key]) {
      return this.props.item.data[key]
    } else {
      return {
        value: '',
        unit: '',
      }
    }
  }

  getValue = (key: string) => {
    const { item } = this.state
    const attr = get(item, `data[${key}]`)

    if (item && !isNil(attr)) {
      if (attr.hasOwnProperty('value')) {
        const value = (attr as IEditableData).value
        if (!isNil(value)) {
          return String(value)
        }
        return ''
      }
      if (!isNil(attr)) {
        return String(attr)
      }
      return ''
    }
    return ''
  }

  getEditUnit = (key: string) => {
    const { item } = this.state
    return get(item, `data[${key}].editData.unit`, undefined)
  }

  getItemUnit = (key: string) => {
    const { item } = this.state
    return get(item, `data[${key}].unit`, undefined)
  }

  public render() {
    const { head, t } = this.props
    const { isEditable, alternatives, saveMode } = this.state

    return (
      <div className="flex">
        {this.state.isActive ? (
          <div className={`w-full flex flex-col ${isEditable ? 'mb-45' : ''}`}>
            <div className={`flex items-end ${isEditable ? 'mb-30' : ''}`}>
              {head.map((headItem: IEditableTableHead) => {
                const { id, productName } = this.state.item
                let item = this.getItem(headItem.key)
                if (item.editable || headItem.editable || headItem.hasOptions) {
                  return (
                    <EditableTableData
                      articleNumber={id}
                      productName={productName}
                      key={headItem.key}
                      name={headItem.key}
                      col={headItem.col}
                      item={item}
                      alternatives={headItem.hasOptions ? alternatives : undefined}
                      placeholder={headItem.placeholder}
                      unit={
                        this.getEditUnit(headItem.key) ||
                        this.getItemUnit(headItem.key) ||
                        headItem.unit
                      }
                      leftAlign={headItem.leftAlign}
                      onChange={(key, value) => this.handleInputChange(key, value)}
                      onOptionChange={this.handleAlternativeChange}
                    />
                  )
                } else {
                  return (
                    <TableData
                      key={headItem.key}
                      name={headItem.key}
                      className="pl-30 first-child:pl-0"
                      col={headItem.col}
                      highlight={headItem.highlight}
                      text={this.getValue(headItem.key)}
                      unit={this.getItemUnit(headItem.key) || headItem.unit}
                    />
                  )
                }
              })}
            </div>
            {isEditable && (
              <>
                <Radio
                  underline
                  label={t('adoptChangesFor')}
                  options={saveModeOptions(t)}
                  value={defaultTo(saveMode, saveModeOptions(t)[0].value)}
                  onChange={(value) => this.handleSaveModeChange(value)}
                />
                <div className="flex items-start mt-30">
                  <Button className="mr-15" primary onClick={this.handleSave}>
                    {t('save')}
                  </Button>
                  <Button secondary onClick={() => this.cancelEdit()}>
                    {t('abort')}
                  </Button>
                </div>
              </>
            )}
          </div>
        ) : (
          head.map((headItem: IEditableTableHead) => {
            return (
              <TableData
                key={headItem.key}
                onClick={() => this.startEdit()}
                name={headItem.key}
                col={headItem.col}
                text={this.getValue(headItem.key)}
                unit={this.getItemUnit(headItem.key) || headItem.unit}
                highlight={headItem.hasOptions || headItem.highlight}
              />
            )
          })
        )}
      </div>
    )
  }
}

export default compose(withPositionAlternativesDataQuery, withTranslation())(EditableTableRow)
